import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, MessageSquare, Image as ImageIcon, BarChart3, ArrowLeft, Send, Upload, Download, X, Eye } from 'lucide-react';

const WebaktePage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [webakte, setWebakte] = useState(null);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Get token based on role
  const customerToken = localStorage.getItem('customer_token');
  const partnerToken = localStorage.getItem('partner_token');
  const lawyerToken = localStorage.getItem('lawyer_token');
  const partnerId = searchParams.get('partner_id') || localStorage.getItem('partner_id'); // Fallback for partners
  
  const token = customerToken || partnerToken || lawyerToken;
  const userRole = customerToken ? 'customer' : partnerToken ? 'partner' : lawyerToken ? 'lawyer' : partnerId ? 'partner' : null;

  useEffect(() => {
    if (!token && !partnerId) {
      navigate('/kunden/login');
      return;
    }
    loadWebakte();
    loadMessages();
    loadDocuments();

    // Poll messages every 5 seconds
    const interval = setInterval(() => {
      loadMessages();
      loadDocuments();
    }, 5000);

    return () => clearInterval(interval);
  }, [caseId]);

  const loadWebakte = async () => {
    try {
      // Build URL with partner_id if no token
      const url = partnerId && !token
        ? `${process.env.REACT_APP_BACKEND_URL}/api/webakte/${caseId}?partner_id=${partnerId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/webakte/${caseId}`;
      
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(url, { headers });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Zugriff verweigert');
      }

      const data = await response.json();
      setWebakte(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      // Build URL with partner_id if no token
      const url = partnerId && !token
        ? `${process.env.REACT_APP_BACKEND_URL}/api/case-chat/${caseId}?partner_id=${partnerId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/case-chat/${caseId}`;
      
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Messages load failed:', err);
    }
  };

  const loadDocuments = async () => {
    try {
      // Build URL with partner_id if no token
      const url = partnerId && !token
        ? `${process.env.REACT_APP_BACKEND_URL}/api/case-documents/${caseId}?partner_id=${partnerId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/case-documents/${caseId}`;
      
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error('Documents load failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Lade Webakte...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border-2 border-red-200 rounded-2xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-700 mb-4">Zugriff verweigert</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={() => navigate(-1)} className="text-brand-green hover:text-brand-green-dark font-semibold underline">
            Zurück
          </button>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: '\u00dcbersicht', icon: BarChart3 },
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    { id: 'documents', label: 'Dokumente', icon: FileText },
    { id: 'photos', label: 'Fotos & Video', icon: ImageIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 font-medium text-sm inline-flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{webakte.license_plate}</h1>
              <p className="text-gray-600">{webakte.vehicle_manufacturer} {webakte.vehicle_model}</p>
            </div>
            <span className="px-4 py-2 rounded-lg text-sm font-bold bg-brand-green/10 text-brand-green border-2 border-brand-green/20">
              {webakte.status}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-semibold text-sm inline-flex items-center gap-2 border-b-2 transition-colors ${ 
                    activeTab === tab.id 
                      ? 'border-brand-green text-brand-green' 
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {activeTab === 'overview' && <OverviewTab webakte={webakte} />}
        {activeTab === 'chat' && <ChatTab caseId={caseId} messages={messages} onMessageSent={loadMessages} token={token} partnerId={partnerId} userRole={userRole} />}
        {activeTab === 'documents' && <DocumentsTab caseId={caseId} documents={documents} onDocumentUploaded={loadDocuments} token={token} partnerId={partnerId} userRole={userRole} />}
        {activeTab === 'photos' && <PhotosTab webakte={webakte} />}
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ webakte }) => {
  const statusExplanations = {
    'draft': 'Fall wird vorbereitet',
    'eingegangen': 'Fall wurde eingereicht und wird geprüft',
    'rueckfrage_offen': 'Wir ben\u00f6tigen noch Informationen von dir',
    'termin_vereinbart': 'Begutachtungstermin wurde vereinbart',
    'begutachtung_durchgefuehrt': 'Fahrzeug wurde begutachtet',
    'gutachten_in_erstellung': 'Gutachten wird erstellt',
    'gutachten_fertig': 'Gutachten ist fertig',
    'abgeschlossen': 'Fall abgeschlossen'
  };

  return (
    <div className="space-y-8">
      {/* Status Timeline */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Status & Fortschritt</h2>
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Aktueller Status:</p>
          <div className="flex items-center gap-3">
            <span className="px-4 py-2 rounded-lg text-base font-bold bg-brand-green/10 text-brand-green border-2 border-brand-green/20">
              {webakte.status}
            </span>
            <p className="text-sm text-gray-700">{statusExplanations[webakte.status] || 'In Bearbeitung'}</p>
          </div>
        </div>

        {/* Timeline */}
        {webakte.timeline && webakte.timeline.length > 0 && (
          <div className="space-y-3 border-l-2 border-gray-200 pl-6 ml-3">
            {webakte.timeline.slice().reverse().map((event, idx) => (
              <div key={idx} className="relative">
                <div className="absolute -left-[27px] w-4 h-4 bg-brand-green rounded-full border-2 border-white"></div>
                <div>
                  <p className="font-semibold text-gray-900">{event.title}</p>
                  <p className="text-xs text-gray-500">{new Date(event.created_at).toLocaleString('de-DE')}</p>
                  {event.message && <p className="text-sm text-gray-600 mt-1">{event.message}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Beteiligte */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Beteiligte</h2>
        <div className="space-y-4">
          {webakte.participants.map((p, idx) => {
            const roleColors = {
              'customer': 'bg-blue-100 text-blue-700',
              'partner': 'bg-purple-100 text-purple-700',
              'lawyer': 'bg-green-100 text-green-700',
              'admin': 'bg-gray-100 text-gray-700'
            };
            const roleLabels = {
              'customer': 'Kunde',
              'partner': 'Partner',
              'lawyer': 'Anwalt',
              'admin': 'Admin'
            };
            return (
              <div key={idx} className="flex items-center justify-between border border-gray-200 rounded-xl p-4">
                <div>
                  <p className="font-semibold text-gray-900">{p.name}</p>
                  <p className="text-sm text-gray-600">{p.email}</p>
                  {p.law_firm && <p className="text-xs text-gray-500">{p.law_firm}</p>}
                </div>
                <span className={`px-3 py-1 rounded-lg text-xs font-bold ${roleColors[p.role]}`}>
                  {roleLabels[p.role]}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fall-Details */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Fall-Details</h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div><span className="font-semibold">Schadentag:</span> {webakte.damage_date ? new Date(webakte.damage_date).toLocaleDateString('de-DE') : 'N/A'}</div>
          <div><span className="font-semibold">Schadenort:</span> {webakte.damage_location || 'N/A'}</div>
          <div><span className="font-semibold">Kilometerstand:</span> {webakte.vehicle_mileage ? `${webakte.vehicle_mileage} km` : 'N/A'}</div>
          <div><span className="font-semibold">Fotos:</span> {webakte.image_count} hochgeladen</div>
          <div className="md:col-span-2"><span className="font-semibold">Beschreibung:</span> {webakte.damage_description || 'N/A'}</div>
        </div>
      </div>
    </div>
  );
};

// Chat Tab
const ChatTab = ({ caseId, messages, onMessageSent, token, partnerId, userRole }) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const url = partnerId && !token
        ? `${process.env.REACT_APP_BACKEND_URL}/api/case-chat/${caseId}?partner_id=${partnerId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/case-chat/${caseId}`;
      
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: newMessage })
      });

      if (!response.ok) {
        throw new Error('Senden fehlgeschlagen');
      }

      setNewMessage('');
      onMessageSent();
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  const roleColors = {
    'customer': 'bg-blue-50 border-blue-200',
    'partner': 'bg-purple-50 border-purple-200',
    'lawyer': 'bg-green-50 border-green-200',
    'admin': 'bg-gray-50 border-gray-200',
    'system': 'bg-yellow-50 border-yellow-200'
  };

  const roleBadgeColors = {
    'customer': 'bg-blue-100 text-blue-700',
    'partner': 'bg-purple-100 text-purple-700',
    'lawyer': 'bg-green-100 text-green-700',
    'admin': 'bg-gray-100 text-gray-700',
    'system': 'bg-yellow-100 text-yellow-700'
  };

  const roleLabels = {
    'customer': 'Kunde',
    'partner': 'Partner',
    'lawyer': 'Anwalt',
    'admin': 'Admin',
    'system': 'System'
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-8 min-h-[600px] flex flex-col">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Chat</h2>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto mb-6 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Noch keine Nachrichten</p>
            <p className="text-sm text-gray-500 mt-2">Starte die Konversation!</p>
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className={`border rounded-xl p-4 ${roleColors[msg.sender_role] || 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-lg text-xs font-bold ${roleBadgeColors[msg.sender_role]}`}>
                    {roleLabels[msg.sender_role]}
                  </span>
                  <span className="font-semibold text-gray-900 text-sm">{msg.sender_name || 'Unbekannt'}</span>
                </div>
                <span className="text-xs text-gray-500">{new Date(msg.created_at).toLocaleString('de-DE')}</span>
              </div>
              <p className="text-gray-800 text-sm">{msg.content}</p>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-3">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Nachricht schreiben..."
          className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
        />
        <button
          onClick={sendMessage}
          disabled={sending || !newMessage.trim()}
          className="px-6 py-3 bg-brand-green hover:bg-brand-green-dark text-white font-semibold rounded-xl inline-flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Documents Tab
const DocumentsTab = ({ caseId, documents, onDocumentUploaded, token, partnerId, userRole }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('sonstiges');
  const [visibility, setVisibility] = useState('all');

  const handleUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('case_id', caseId);
      formData.append('document_type', selectedType);
      formData.append('visibility', visibility);
      formData.append('file', file);
      if (partnerId && !token) {
        formData.append('partner_id', partnerId);
      }

      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-documents/upload`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Upload fehlgeschlagen');
      }

      onDocumentUploaded();
      alert('Dokument hochgeladen');
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-documents/download/${docId}`, {
        headers
      });

      if (!response.ok) throw new Error('Download fehlgeschlagen');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
    } catch (err) {
      alert(err.message);
    }
  };

  const docTypes = [
    { value: 'versicherung', label: 'Versicherungsschreiben' },
    { value: 'polizei', label: 'Polizeibericht' },
    { value: 'reparatur', label: 'Reparatur / Kostenvoranschlag' },
    { value: 'gutachten', label: 'Gutachten' },
    { value: 'anwalt', label: 'Anwaltsschreiben' },
    { value: 'fotos', label: 'Fotos / Nachweise' },
    { value: 'sonstiges', label: 'Sonstiges' }
  ];

  return (
    <div className="space-y-8">
      {/* Upload */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Dokument hochladen</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Kategorie</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
            >
              {docTypes.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>

          {userRole === 'admin' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Sichtbarkeit</label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
              >
                <option value="all">Alle Beteiligten</option>
                <option value="admin_only">Nur Admin</option>
                <option value="lawyer_admin">Anwalt + Admin</option>
              </select>
            </div>
          )}
        </div>

        <label className="block cursor-pointer">
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-brand-green transition-all">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-700 font-medium mb-1">Datei ausw\u00e4hlen</p>
            <p className="text-xs text-gray-500">PDF, JPG, PNG, MP4 (max. 25MB)</p>
          </div>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.mp4"
            onChange={(e) => handleUpload(e.target.files[0])}
            className="hidden"
            disabled={uploading}
          />
        </label>
        {uploading && <p className="text-center text-brand-green mt-4">Uploade...</p>}
      </div>

      {/* Documents List */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Alle Dokumente</h2>
        {documents.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Noch keine Dokumente hochgeladen</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{doc.original_filename}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                    <span>{docTypes.find(dt => dt.value === doc.document_type)?.label || doc.document_type}</span>
                    <span>\u2022</span>
                    <span>{doc.uploaded_by_name}</span>
                    <span>\u2022</span>
                    <span>{new Date(doc.created_at).toLocaleDateString('de-DE')}</span>
                    {doc.visibility !== 'all' && (
                      <><span>\u2022</span><span className="text-orange-600 font-medium">Intern</span></>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(doc.id, doc.original_filename)}
                  className="px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Photos Tab
const PhotosTab = ({ webakte }) => {
  const images = webakte.images || [];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-8">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Fotos & Video</h2>

      {/* Video */}
      {webakte.video_url && (
        <div className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-3">Video-Rundgang</h3>
          <div className="bg-gray-900 rounded-xl overflow-hidden">
            <video controls className="w-full max-w-2xl mx-auto">
              <source src={`${process.env.REACT_APP_BACKEND_URL}${webakte.video_url}`} type="video/mp4" />
            </video>
          </div>
        </div>
      )}

      {/* Photos */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Fotos ({images.length})</h3>
        {images.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-xl">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Noch keine Fotos hochgeladen</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {images.map((img, idx) => {
              const imgUrl = img.url || img.image_url || img;
              const fullUrl = imgUrl?.startsWith('http') ? imgUrl : `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
              
              return (
                <div key={idx} className="relative group">
                  <img
                    src={fullUrl}
                    alt={img.slot_key || `Bild ${idx + 1}`}
                    className="w-full h-48 object-cover rounded-xl border border-gray-200"
                    onError={(e) => {
                      console.error('Image load failed:', imgUrl);
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23f3f4f6" width="400" height="300"/><text x="50%" y="50%" text-anchor="middle" fill="%239ca3af" font-size="14">Bild nicht verfügbar</text></svg>';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                    <button 
                      onClick={() => window.open(fullUrl, '_blank')}
                      className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold text-sm inline-flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Ansehen
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2 font-medium">{img.slot_key || `Bild ${idx + 1}`}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebaktePage;
